import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import blogApi from "services/blogApi.js";
import "assets/css/blog-content.css";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: "70px",
    paddingBottom: "50px",
    maxWidth: "1140px",
    margin: "0 auto",
    padding: "0 20px"
  },
  title: {
    fontSize: "2.5rem",
    fontWeight: "300",
    marginBottom: "50px",
    textAlign: "center",
    color: "#3C4858"
  },
  sectionTitle: {
    fontSize: "2rem",
    fontWeight: "400",
    marginBottom: "30px",
    color: "#3C4858"
  },
  section: {
    marginBottom: "80px"
  },
  postCard: {
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    marginBottom: "30px",
    cursor: "pointer",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)"
    }
  },
  postImage: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderRadius: "8px",
    marginBottom: "15px"
  },
  placeholderImage: {
    width: "100%",
    height: "200px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    fontSize: "0.9rem"
  },
  postMeta: {
    fontSize: "0.875rem",
    color: "#999",
    marginBottom: "10px"
  },
  postTitle: {
    fontSize: "1.25rem",
    fontWeight: "500",
    marginBottom: "15px",
    color: "#3C4858",
    textDecoration: "none",
    lineHeight: "1.4",
    "&:hover": {
      color: "#f7931e",
      textDecoration: "none"
    }
  },
  postExcerpt: {
    fontSize: "0.95rem",
    lineHeight: "1.6",
    color: "#666",
    marginBottom: "0",
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    overflow: "hidden"
  },
  filterSection: {
    marginBottom: "30px",
    display: "flex",
    alignItems: "center",
    gap: "20px"
  },
  filterControl: {
    minWidth: "200px"
  },
  loadingText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#999",
    marginTop: "50px"
  },
  errorText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#f44336",
    marginTop: "50px"
  },
  noPostsText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#999",
    marginTop: "50px"
  }
}));

export default function BlogList() {
  const classes = useStyles();
  const [latestPosts, setLatestPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [postsResponse, categoriesResponse] = await Promise.all([
          blogApi.getRecentPosts(6),
          blogApi.getPopularCategories(6)
        ]);
        
        if (postsResponse.success) {
          setLatestPosts(postsResponse.data);
        }
        
        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data);
        }
        
        // Load all posts for initial filter view
        const allPostsResponse = await blogApi.getAllPosts();
        if (allPostsResponse.success) {
          setFilteredPosts(allPostsResponse.data);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleCategoryFilter = async (categoryId) => {
    try {
      setFilterLoading(true);
      setSelectedCategory(categoryId);
      
      if (categoryId === '') {
        // Show all posts
        const response = await blogApi.getAllPosts();
        if (response.success) {
          setFilteredPosts(response.data);
        }
      } else {
        // Filter by category
        const response = await blogApi.getPostsByCategory(categoryId);
        if (response.success) {
          setFilteredPosts(response.data);
        }
      }
    } catch (err) {
      console.error('Error filtering posts:', err);
    } finally {
      setFilterLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderPostCard = (post) => (
    <div key={post.id} className={classes.postCard}>
      <Link to={`/blog/post/${post.id}`} style={{ textDecoration: 'none' }}>
        {post.featured_image ? (
          <img 
            src={post.featured_image} 
            alt={post.title}
            className={classes.postImage}
          />
        ) : (
          <div className={classes.placeholderImage}>
            No Image Available
          </div>
        )}
        
        <div className={classes.postMeta}>
          {formatDate(post.created_at)}
        </div>
        
        <h3 className={classes.postTitle}>
          {post.title}
        </h3>
        
        <div 
          className={classes.postExcerpt}
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      </Link>
    </div>
  );

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingText}>Loading blog posts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <div className={classes.errorText}>Error loading posts: {error}</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h1 className={classes.title}>WeVett Realty Blog</h1>

      {/* Latest Posts Section */}
      <div className={classes.section}>
        <h2 className={classes.sectionTitle}>Latest Posts</h2>
        {latestPosts.length === 0 ? (
          <div className={classes.noPostsText}>No recent posts found.</div>
        ) : (
          <GridContainer>
            {latestPosts.map((post) => (
              <GridItem xs={12} sm={6} md={4} key={post.id}>
                {renderPostCard(post)}
              </GridItem>
            ))}
          </GridContainer>
        )}
      </div>

      {/* Filter Section */}
      <div className={classes.section}>
        <h2 className={classes.sectionTitle}>All Posts</h2>
        
        <div className={classes.filterSection}>
          <FormControl className={classes.filterControl}>
            <InputLabel>Filter By Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => handleCategoryFilter(e.target.value)}
              disabled={filterLoading}
            >
              <MenuItem value="">All Posts</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name} ({category.post_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {filterLoading && <span>Loading...</span>}
        </div>

        {filteredPosts.length === 0 ? (
          <div className={classes.noPostsText}>No posts found for the selected filter.</div>
        ) : (
          <GridContainer>
            {filteredPosts.map((post) => (
              <GridItem xs={12} sm={6} md={4} key={post.id}>
                {renderPostCard(post)}
              </GridItem>
            ))}
          </GridContainer>
        )}
      </div>
    </div>
  );
}